// 批次 19（B8-1，Gitea issue #14）：dashboard 頁型的共用零件（6 個角色首頁變體共用）。
//
// 對照 legacy mustache 模板群（mainPageTemplate.jsp，582 行版）＋ mainUtils.js 共用引擎：
//   - 公告欄（studentMain.jsp:60-77 的表＋mainUtils.js:62-77 renderAnnouncements＋
//     studentMain.js:42-54 More/Less）
//   - menuRow 盒群（menu_Box_Tmpl:5／menu_Box_teacher_Tmpl:18／menu_Box_calendar_Tmpl:201；
//     studentMain.js:225-298 renderMenu／renderCalendarMenu）
//   - 訓練資訊 panel（studentMain.jsp:84-123；mainUtils.js:88 renderStudentPlanPanel／
//     phase_Tmpl:229／phase_Box_Tmpl:275）
// 欄位名與文案逐字對照 legacy；圖片 img/main/*.png 以 placeholder 呈現
// 【雛形值待實機校準】（素材無法自 legacy 抽出）。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useState, type ReactElement } from 'react';
import { ContentPanel } from '@/components/layouts';
import type {
  DashboardAnnouncement,
  DashboardCalendarEvent,
  DashboardFormTargetBox,
  DashboardMenuTeacher,
  DashboardStudentListItem,
  TeacherStudentDetail,
} from '@/mocks/dashboard-store';

/** 公告顯示筆數上限（mainUtils.js:10 `const announcesLimit = 3`）。 */
export const ANNOUNCES_LIMIT = 3;

/** 月份英文簡寫（mainUtils.js:21 monthNames）。 */
const MONTH_NAMES = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May.', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];

// ---------------------------------------------------------------------------
// 公告欄（studentMain.jsp:60-77 的 .announcementBox）
// ---------------------------------------------------------------------------

export interface AnnouncementPanelProps {
  readonly pageId: string;
  readonly announcements: readonly DashboardAnnouncement[];
  /** 點主旨連結（legacy `a.announcement` click → showAnnounce(id)＋未讀移除）。 */
  readonly onOpenAnnouncement: (id: string) => void;
}

/**
 * 公告欄 panel。legacy：3 欄表（NO. 5%／主旨 75%／公告時間 20%，studentMain.jsp:63-71），
 * `announcesLimit=3` 以外隱藏（mainUtils.js:75-76），More＝全顯／Less＝收回
 * （studentMain.js:42-54），未讀列主旨後有 `(未讀)` span（mainUtils.js:66-68，判定＝
 * `announcementId` 欄位不存在——legacy 原樣）。
 */
export function AnnouncementPanel({
  pageId,
  announcements,
  onOpenAnnouncement,
}: AnnouncementPanelProps): ReactElement {
  const [expanded, setExpanded] = useState(false);
  return (
    <ContentPanel uiId={`${pageId}-announcement-panel`} title="公告欄">
      <div className="mb-2">
        <table data-ui-id={`${pageId}-announcement-table`} className="mb-5 w-full border-collapse text-ink">
          <thead>
            <tr>
              <th data-ui-id={`${pageId}-announcement-col-no`} style={{ width: '5%' }} className="p-[18px] align-bottom font-bold">
                NO.
              </th>
              <th data-ui-id={`${pageId}-announcement-col-subject`} style={{ width: '75%' }} className="p-[18px] align-bottom font-bold">
                主旨
              </th>
              <th data-ui-id={`${pageId}-announcement-col-time`} style={{ width: '20%' }} className="p-[18px] align-bottom font-bold">
                公告時間
              </th>
            </tr>
          </thead>
          <tbody>
            {announcements.map((announcement, index) => (
              <tr
                key={announcement.id}
                data-ui-id={`${pageId}-announcement-row-${announcement.id}`}
                style={index >= ANNOUNCES_LIMIT && !expanded ? { display: 'none' } : undefined}
              >
                <td className="border-t border-border p-[18px]">{index + 1}</td>
                <td className="border-t border-border p-[18px]">
                  <a
                    href="#"
                    data-ui-id={`${pageId}-announcement-link-${announcement.id}`}
                    data-announce-id={announcement.id}
                    onClick={(event) => {
                      event.preventDefault();
                      onOpenAnnouncement(announcement.id);
                    }}
                  >
                    {announcement.subject}
                  </a>
                  {announcement.announcementId === undefined ? (
                    <span data-ui-id={`${pageId}-announcement-unread-${announcement.id}`}>(未讀)</span>
                  ) : null}
                </td>
                <td className="border-t border-border p-[18px]">{announcement.startFormatDate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-right">
        {expanded ? (
          <button
            type="button"
            data-ui-id={`${pageId}-announcement-less`}
            onClick={() => setExpanded(false)}
          >
            Less
          </button>
        ) : null}
        {expanded ? null : (
          <button
            type="button"
            data-ui-id={`${pageId}-announcement-more`}
            onClick={() => setExpanded(true)}
          >
            More
          </button>
        )}
      </div>
    </ContentPanel>
  );
}

// ---------------------------------------------------------------------------
// menuRow 盒群（menu_Box_Tmpl:5／menu_Box_teacher_Tmpl:18／menu_Box_calendar_Tmpl:201）
// ---------------------------------------------------------------------------

export interface TodoMenuBoxProps {
  readonly pageId: string;
  /** menu_Box_Tmpl 的 {{class}}：盒的 legacy class（clickable 等併入）。 */
  readonly boxClass: string;
  /** 圖片來源（img/main/*.png；placeholder 以文字呈現）。 */
  readonly imgSrc: string;
  readonly menuText: string;
  /** dataBox 顯示值（＝incompleteQuantity）。 */
  readonly displayText: string | number;
  /** 未提供＝S0 骨架（不可點）；S1 各變體接導航。 */
  readonly onClick?: (() => void) | undefined;
}

/** 待辦事項盒（menu_Box_Tmpl:5-16：iconBox 圖＋iconText／dataBox displayText）。 */
export function TodoMenuBox({
  pageId,
  boxClass,
  imgSrc,
  menuText,
  displayText,
  onClick,
}: TodoMenuBoxProps): ReactElement {
  return (
    <div
      data-ui-id={`${pageId}-menu-box-${kebab(boxClass)}`}
      className={`inline-flex min-w-[220px] items-stretch border border-border ${onClick !== undefined ? 'cursor-pointer' : ''} ${boxClass}`}
      onClick={onClick}
    >
      <div data-ui-id={`${pageId}-menu-box-${kebab(boxClass)}-icon`} className="flex w-[35%] flex-col items-center justify-center p-2">
        <span
          data-ui-id={`${pageId}-menu-box-${kebab(boxClass)}-img`}
          title={imgSrc}
          className="text-[28px] leading-none"
        >
          ▣
        </span>
        <p className="text-center text-[13px]">{menuText}</p>
      </div>
      <div
        data-ui-id={`${pageId}-menu-box-${kebab(boxClass)}-data`}
        className="flex w-[65%] items-center justify-center border-l border-border p-2"
      >
        <p className="text-center text-[16px]">{displayText}</p>
      </div>
    </div>
  );
}

export interface TeacherMenuBoxProps {
  readonly pageId: string;
  /** menu_Box_teacher_Tmpl 的 {{type}}：mentors／clinicalTeachers。 */
  readonly type: 'mentors' | 'clinicalTeachers';
  readonly imgSrc: string;
  readonly menuText: string;
  readonly teachers: readonly DashboardMenuTeacher[];
  /** 點教師名（legacy p.teacherName data-id；原型以選擇回呼呈現）。 */
  readonly onSelectTeacher?: (teacher: DashboardMenuTeacher) => void;
}
/** 教師盒（menu_Box_teacher_Tmpl:18-35：icon＋教師名單 name＋extNo，data-id）。 */
export function TeacherMenuBox({
  pageId,
  type,
  imgSrc,
  menuText,
  teachers,
  onSelectTeacher,
}: TeacherMenuBoxProps): ReactElement {
  const typeKey = kebab(type); // data-ui-id kebab-case（§10.1）
  return (
    <div
      data-ui-id={`${pageId}-menu-box-${typeKey}`}
      className={`inline-flex min-w-[220px] items-stretch border border-border ${type}`}
    >
      <div data-ui-id={`${pageId}-menu-box-${typeKey}-icon`} className="flex w-[35%] flex-col items-center justify-center p-2">
        <span data-ui-id={`${pageId}-menu-box-${typeKey}-img`} title={imgSrc} className="text-[28px] leading-none">
          ▣
        </span>
        <p className="text-center text-[13px]">{menuText}</p>
      </div>
      <div
        data-ui-id={`${pageId}-menu-box-${typeKey}-data`}
        className="flex w-[65%] flex-col items-center justify-center border-l border-border p-2"
      >
        {teachers.map((teacher) => (
          <p
            key={teacher.id}
            data-ui-id={`${pageId}-menu-box-${typeKey}-teacher-${kebab(teacher.id)}`}
            data-teacher-id={teacher.id}
            className="cursor-pointer text-center text-[13px]"
            onClick={() => onSelectTeacher?.(teacher)}
          >
            {teacher.name}
            {teacher.extNo ? (
              <>
                <br />
                {teacher.extNo}
              </>
            ) : null}
          </p>
        ))}
      </div>
    </div>
  );
}

export interface CalendarMenuBoxProps {
  readonly pageId: string;
  readonly now: Date;
  /** 未來行程（renderCalendarMenu 過濾後；studentMain.js:309）。 */
  readonly schedules: readonly DashboardCalendarEvent[];
  /** 未提供＝S0 骨架（不可點）；S1 接行事曆 modal。 */
  readonly onClick?: () => void;
}

/**
 * 行事曆盒（menu_Box_calendar_Tmpl:201-227：左側日曆圖形＝top 三色條＋`{{day}}<br>{{date}}`、
 * 右側 schedules 底色列 `{{startTime}} {{title}}`）。day＝monthNames[m]、date＝今日日。
 */
export function CalendarMenuBox({ pageId, now, schedules, onClick }: CalendarMenuBoxProps): ReactElement {
  const day = MONTH_NAMES[now.getMonth()];
  const date = now.getDate();
  return (
    <div
      data-ui-id={`${pageId}-menu-box-calendar`}
      data-day={day}
      data-date={String(date)}
      className="inline-flex min-w-[220px] cursor-pointer items-stretch border border-border"
      onClick={onClick}
    >
      <div data-ui-id={`${pageId}-menu-box-calendar-icon`} className="flex w-[35%] flex-col items-center justify-center p-2">
        <div data-ui-id={`${pageId}-menu-box-calendar-card`} className="w-[64px] rounded border border-border">
          <div className="flex gap-[2px] p-[3px]" data-ui-id={`${pageId}-menu-box-calendar-top`}>
            <span className="h-[6px] flex-1 bg-[#bb5548]" />
            <span className="h-[6px] flex-1 bg-[#4b7ba7]" />
            <span className="h-[6px] flex-1 bg-[#87a96b]" />
          </div>
          <div className="p-1 text-center" data-ui-id={`${pageId}-menu-box-calendar-day`}>
            <h1 className="text-[13px] font-bold leading-tight">
              {day}
              <br />
              {date}
            </h1>
          </div>
        </div>
      </div>
      <div
        data-ui-id={`${pageId}-menu-box-calendar-data`}
        className="flex w-[65%] flex-col items-center justify-center gap-1 border-l border-border p-2"
      >
        {schedules.map((schedule) => (
          <p
            key={schedule.id}
            data-ui-id={`${pageId}-menu-box-calendar-schedule-${kebab(schedule.id)}`}
            className="w-full rounded px-1 text-center text-[12px] text-white"
            style={{ backgroundColor: schedule.backgroundColor }}
          >
            {schedule.start.slice(11, 16)} {schedule.title}
          </p>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 教師視角盒群（S2；menu_Box_student_Tmpl:52／menu_Box_studentInfo_Tmpl:185／
// student_grade_Tmpl:284／studentPanel_tmpl:303）
// ---------------------------------------------------------------------------

export interface StudentMenuBoxProps {
  readonly pageId: string;
  /** menu_Box_student_Tmpl 的 data-teacherType（MENTOR／CLINICAL；teacherMain.js:314-325）。 */
  readonly teacherType: 'MENTOR' | 'CLINICAL';
  readonly imgSrc: string;
  readonly menuText: string;
  /** 過/現切換（legacy isClinical；導師強制 current，teacherMain.js:244-247）。 */
  readonly showTimeToggle: boolean;
  /** C1/C2 年級鈕（dentisit student_M/C 模板無此鈕：dentisit/teacherMain.js:335/344） */
  readonly showGradeToggle: boolean;
  /** 名單是否依年級過濾（dentisit genStudents 不過濾，全列；teacherMain.js:339-341 vs dentisit :324-329）。 */
  readonly filterByGrade: boolean;
  /** data-current／data-past 兩份名單（teacherMain.js:326-330）。 */
  readonly currentStudents: readonly DashboardStudentListItem[];
  readonly pastStudents: readonly DashboardStudentListItem[];
  /** 點學員（teacherMain.js:94-105：getStudentData(id,planInstId,teacherType)）。 */
  readonly onSelectStudent: (student: DashboardStudentListItem, time: 'past' | 'current', grade: 'C1' | 'C2') => void;
}

/**
 * 學員名單盒（menu_Box_student_Tmpl:52-88：icon 35%＋dataBox 65%＝過/現圓鈕（isClinical）＋
 * C1/C2 年級鈕＋學員名單；teacherMain.js:232-251 renderStudents：導生名單（isClinical=false）
 * 與指導學生名單（isClinical=true，含過往名單）；預設顯示 current＋C1（:339-341 過濾
 * expansion1==='C1'）。
 */
export function StudentMenuBox({
  pageId,
  teacherType,
  imgSrc,
  menuText,
  showTimeToggle,
  showGradeToggle,
  filterByGrade,
  currentStudents,
  pastStudents,
  onSelectStudent,
}: StudentMenuBoxProps): ReactElement {
  const [time, setTime] = useState<'past' | 'current'>('current');
  const [grade, setGrade] = useState<'C1' | 'C2'>('C1');
  const source = time === 'past' ? pastStudents : currentStudents;
  const students = filterByGrade ? source.filter((student) => student.expansion1 === grade) : source;
  return (
    <div
      data-ui-id={`${pageId}-menu-box-student-${kebab(teacherType)}`}
      data-teacher-type={teacherType}
      className="inline-flex min-w-[260px] items-stretch border border-border"
    >
      <div data-ui-id={`${pageId}-menu-box-student-${kebab(teacherType)}-icon`} className="flex w-[35%] flex-col items-center justify-center p-2">
        <span data-ui-id={`${pageId}-menu-box-student-${kebab(teacherType)}-img`} title={imgSrc} className="text-[28px] leading-none">
          ▣
        </span>
        <p className="text-center text-[13px]">{menuText}</p>
      </div>
      <div data-ui-id={`${pageId}-menu-box-student-${kebab(teacherType)}-data`} className="w-[65%]">
        <div className="flex h-[30%] items-center justify-start gap-3 p-1">
          {showTimeToggle ? (
            <span className="flex gap-1">
              <button
                type="button"
                title="過往學員名單"
                data-ui-id={`${pageId}-student-time-past-${kebab(teacherType)}`}
                className={`h-[22px] w-[22px] rounded-full border text-[11px] ${time === 'past' ? 'border-[#337ab7] bg-[#337ab7] text-white' : 'border-border bg-white text-ink'}`}
                onClick={() => setTime('past')}
              >
                過
              </button>
              <button
                type="button"
                title="現在進行中學員名單"
                data-ui-id={`${pageId}-student-time-current-${kebab(teacherType)}`}
                className={`h-[22px] w-[22px] rounded-full border text-[11px] ${time === 'current' ? 'border-[#337ab7] bg-[#337ab7] text-white' : 'border-border bg-white text-ink'}`}
                onClick={() => setTime('current')}
              >
                現
              </button>
            </span>
          ) : null}
          {showGradeToggle ? (
            <span className="flex gap-1">
              <button
                type="button"
                title="五年級"
                data-ui-id={`${pageId}-student-grade-c-1-${kebab(teacherType)}`}
              className={`h-[22px] w-[22px] rounded-full border text-[11px] ${grade === 'C1' ? 'border-[#5cb85c] bg-[#5cb85c] text-white' : 'border-border bg-white text-ink'}`}
              onClick={() => setGrade('C1')}
            >
              C1
            </button>
            <button
              type="button"
              title="六年級"
              data-ui-id={`${pageId}-student-grade-c-2-${kebab(teacherType)}`}
              className={`h-[22px] w-[22px] rounded-full border text-[11px] ${grade === 'C2' ? 'border-[#5cb85c] bg-[#5cb85c] text-white' : 'border-border bg-white text-ink'}`}
              onClick={() => setGrade('C2')}
            >
              C2
            </button>
            </span>
          ) : null}
        </div>
        <div data-ui-id={`${pageId}-students-${kebab(teacherType)}`} className="mt-1 flex flex-col gap-[2px] px-2 pb-2">
          {students.map((student) => (
            <p
              key={student.id}
              data-ui-id={`${pageId}-student-${kebab(student.id)}-${kebab(teacherType)}`}
              data-student-id={student.id}
              data-plan-inst-id={student.planInstId}
              className="cursor-pointer text-[13px]"
              onClick={() => onSelectStudent(student, time, grade)}
            >
              <span>{student.name}</span>
              {student.extNo ? (
                <>
                  <br />
                  <span>{student.extNo}</span>
                </>
              ) : null}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

/** 燈號色（green/yellow/red；legacy circle class gpaLight/epaLight＋gpaLightColor）。 */
export const LIGHT_COLOR_CLASS: Record<string, string> = {
  green: 'bg-[#5cb85c]',
  yellow: 'bg-[#f0ad4e]',
  red: 'bg-[#d9534f]',
};

export interface GradeLightsProps {
  readonly pageId: string;
  readonly detail: TeacherStudentDetail;
  /** title 提示（legacy tooltipster；雛形以 title 屬性呈現——D19-x 註記）。 */
}

/**
 * 學員成績燈號（student_grade_Tmpl:284-301：困（difficulty）＋等第制 gpaLight＋EPA epaLight；
 * tooltip 內容＝bindGradeTooltipster（teacherMain.js:251-293）：困＝processDifficulyReason、
 * 等第制＝評核成績B(含)以下：…／gapLightRule、EPA＝成績2分(含)以下：…／epaLightRule_C1|C2）。
 */
export function GradeLights({ pageId, detail }: GradeLightsProps): ReactElement {
  const difficultReason = (detail.difficulyReason ?? '').replace(/;/g, '<br>');
  const gpaFailText =
    detail.gpaFailDeptNames !== undefined && detail.gpaFailDeptNames.length > 0
      ? `評核成績B(含)以下：${detail.gpaFailDeptNames.join('，')}`
      : '實習期間等第制成績一次B(含)以下亮黃燈，兩次(含)以上亮紅燈，非上述狀態則呈現綠燈。';
  const epaRule =
    detail.grade === 'C2'
      ? '實習期間EPA成績一次3分(含)亮黃燈，兩次以上(含)亮紅燈，C1&C2有不同亮燈標準，非上述 狀態則呈現綠燈。'
      : '實習期間EPA成績一次2分(含)亮黃燈，兩次以上(含)亮紅燈，C1&C2有不同亮燈標準，非上述 狀態則呈現綠燈。';
  const epaFailText =
    detail.epaFailDeptNames !== undefined && detail.epaFailDeptNames.length > 0
      ? `成績2分(含)以下：${detail.epaFailDeptNames.join('，')}`
      : epaRule;
  return (
    <div data-ui-id={`${pageId}-grade-lights`} className="flex items-center justify-center gap-3 py-1">
      {detail.difficulty === true ? (
        <span
          data-ui-id={`${pageId}-grade-difficult`}
          title={difficultReason}
          className="flex h-[28px] w-[28px] items-center justify-center rounded-full bg-[#f2b3b3] text-[12px]"
        >
          困
        </span>
      ) : null}
      <span className="text-[13px]">等第制</span>
      <span
        data-ui-id={`${pageId}-grade-gpa-light`}
        title={gpaFailText}
        className={`h-[16px] w-[16px] rounded-full ${LIGHT_COLOR_CLASS[detail.gpaLightColor]}`}
      />
      <span className="text-[13px]">EPA</span>
      <span
        data-ui-id={`${pageId}-grade-epa-light`}
        title={epaFailText}
        className={`h-[16px] w-[16px] rounded-full ${LIGHT_COLOR_CLASS[detail.epaLightColor]}`}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 訓練資訊 panel 的盒群（phase_Tmpl:229／phase_Box_Tmpl:275）
// ---------------------------------------------------------------------------

/** 直立盒高度（mainUtils.js:27 getBoxHeightPercent＝100−completePercent）。 */
export function boxHeightPercent(completePercent: number): string {
  return String(parseFloat(String(100 - completePercent)).toPrecision(3));
}

export interface PhaseBoxProps {
  readonly pageId: string;
  readonly kind: 'planForm' | 'classForm' | 'gpa' | 'epa' | 'phaseScore' | 'coreSkill' | 'clinicalArrangement';
  readonly index: number;
  readonly target: DashboardFormTargetBox;
  readonly clickable: boolean;
  readonly onClick: () => void;
}

/**
 * 直立完成度盒（phase_Box_Tmpl:275-283：`height:{{height}}%`＋span.phaseBoxText；
 * mainUtils.js:252 setClassByComplatePercent：0%＝bg-grey、其他＝phaseBox-bg-green，
 * coreSkill 特例＝progress-bg-yellow（mainUtils.js:31-36））。
 */
export function PhaseBox({ pageId, kind, index, target, clickable, onClick }: PhaseBoxProps): ReactElement {
  const height = boxHeightPercent(target.completePercent);
  const kindKey = kebab(kind); // data-ui-id kebab-case（§10.1）
  const toneClass =
    target.completePercent === 0
      ? 'bg-[#f2f2f2]'
      : kind === 'coreSkill'
        ? 'bg-[#f7e28b]'
        : 'bg-[#dff0d8]';
  return (
    <div
      data-ui-id={`${pageId}-box-${kindKey}-${index}`}
      className={`mx-[3px] flex h-[120px] w-[90px] flex-col justify-end border border-border align-bottom ${toneClass} ${clickable ? 'cursor-pointer' : ''}`}
      onClick={clickable ? onClick : undefined}
    >
      <div data-ui-id={`${pageId}-box-${kindKey}-${index}-inner`} style={{ height: `${height}%` }}>
        <span
          data-ui-id={`${pageId}-box-${kindKey}-${index}-text`}
          className="text-center text-[12px]"
          dangerouslySetInnerHTML={{ __html: target.displayText }}
        />
      </div>
    </div>
  );
}

export interface PhaseItemProps {
  readonly pageId: string;
  readonly index: number;
  readonly phase: {
    readonly phaseTargetName: string;
    readonly currentPhase: boolean;
    readonly showData: boolean;
    readonly phaseDeptName: string;
    readonly phasePeriod: string;
    readonly phasePercent: number;
  };
  /** 進度條內顯示名（dentisit/MA 版＝tradeText(phaseTargetName)，mainUtils.js:155/:187）。 */
  readonly displayName?: string | undefined;
}

/**
 * 階段進度條列（phase_Tmpl:229-243：li.phase title=phaseTargetName isCurrent=currentPhase、
 * .progress width=phasePercent%、phaseDeptName／phasePeriod；currentPhase 高亮、
 * !showData 灰化——mainUtils.js:95-99）。
 */
export function PhaseItem({ pageId, index, phase, displayName }: PhaseItemProps): ReactElement {
  const tone = phase.currentPhase
    ? 'bg-[#5cb85c]'
    : phase.showData
      ? 'bg-[#8bc4ea]'
      : 'bg-[#cccccc]';
  return (
    <li
      data-ui-id={`${pageId}-phase-${index}`}
      data-is-current={phase.currentPhase ? 'true' : 'false'}
      title={phase.phaseTargetName}
      className="min-w-[140px] list-none px-1"
    >
      <div data-ui-id={`${pageId}-phase-${index}-arrow`} className="border-l-2 border-transparent">
        <div
          data-ui-id={`${pageId}-phase-${index}-progress`}
          className={`h-[52px] rounded ${tone}`}
          style={{ width: `${phase.phasePercent}%` }}
        >
          <p className="px-1 text-[12px] leading-tight text-white">
            <span data-ui-id={`${pageId}-phase-${index}-name`}>{displayName ?? phase.phaseDeptName}</span>
            <br />
            <span data-ui-id={`${pageId}-phase-${index}-period`}>{phase.phasePeriod}</span>
          </p>
        </div>
      </div>
    </li>
  );
}

/** 盒群專用：把 legacy class／id 轉 kebab-case（§10.1 data-ui-id 規則）。 */
export function kebab(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

/** 階段名截斷（mainUtils.js:621-627 tradeText：超過 9 字取前 8 字＋"..."；dentisit/MA 版
 *  substage/ma 模板的 showPhaseTargetName）。 */
export function tradeText(value: string): string {
  const len = 9;
  if (value.length > len) {
    return `${value.substring(0, len - 1)}...`;
  }
  return value;
}
